import apiClient from './api';
import { getCsrfTokenFromCookie } from 'core/utils/csrf';
import { logError } from 'core/utils/logger';
import { websocketService } from './websocketService';
import { 
  ChatSession, 
  ChatSessionCreate, 
  ChatMessage, 
  ChatCompletionRequest 
} from '../types/api.types';

/**
 * Chat service for interacting with the chat endpoints
 */
export class ChatService {
  private baseUrl = '/pentest/ui/chat';

  /**
   * Get all chat sessions for a project
   */
  async getChatSessions(projectId: string): Promise<ChatSession[]> {
    return apiClient.get<ChatSession[]>(`${this.baseUrl}/sessions/project/${projectId}/`);
  }
  
  /**
   * Get or create a single chat session for a project
   * This is used to ensure each project has exactly one chat session
   */
  async getOrCreateProjectSession(projectId: string): Promise<ChatSession> {
    try {
      // Get existing sessions
      const sessions = await this.getChatSessions(projectId);
      
      // If there's at least one session, return the first one
      if (sessions && sessions.length > 0) {
        return sessions[0];
      }
      
      // If no sessions exist, create a new one
      return this.createChatSession({
        project_id: projectId,
        title: `Project Chat`
      });
    } catch (error) {
      logError('Failed to get or create project session', error);
      throw error;
    }
  }

  /**
   * Get a chat session by ID
   */
  async getChatSession(sessionId: string): Promise<ChatSession> {
    return apiClient.get<ChatSession>(`${this.baseUrl}/sessions/${sessionId}/`);
  }

  /**
   * Create a new chat session
   */
  async createChatSession(session: ChatSessionCreate): Promise<ChatSession> {
    const csrfToken = getCsrfTokenFromCookie();
    return apiClient.post<ChatSession>(`${this.baseUrl}/sessions/`, session, {
      headers: { 'X-CSRF-Token': csrfToken }
    });
  }

  /**
   * Get all messages for a chat session
   */
  async getMessages(sessionId: string): Promise<ChatMessage[]> {
    console.log(`ENDPOINT: ${this.baseUrl}/messages/session/${sessionId}/`);
    return apiClient.get<ChatMessage[]>(`${this.baseUrl}/messages/session/${sessionId}/`);
  }

  /**
   * Send a message and get a streaming completion
   * This uses WebSockets for streaming responses with SSE as fallback
   */
  sendMessageStreaming(request: ChatCompletionRequest, onMessage: (text: string) => void, onDone: () => void, onError: (error: any) => void): () => void {
    console.log('Setting up streaming chat completion with request:', request);
    
    // Validate the request before sending
    try {
      if (!request.project_id || request.project_id.trim() === '') {
        onError(new Error('Project ID is required and cannot be empty'));
        return () => {};
      }
      
      if (!request.session_id || request.session_id.trim() === '') {
        onError(new Error('Session ID is required and cannot be empty'));
        return () => {};
      }
      
      if (!request.message || request.message.trim() === '') {
        onError(new Error('Message is required and cannot be empty'));
        return () => {};
      }
      
      // Validate ID format (should contain only alphanumeric, hyphens, underscores)
      const idRegex = /^[a-zA-Z0-9_-]+$/;
      if (!idRegex.test(request.project_id)) {
        onError(new Error(`Invalid project ID format: "${request.project_id}". Only letters, numbers, hyphens, and underscores are allowed.`));
        return () => {};
      }
      
      if (!idRegex.test(request.session_id)) {
        onError(new Error(`Invalid session ID format: "${request.session_id}". Only letters, numbers, hyphens, and underscores are allowed.`));
        return () => {};
      }
    } catch (validationError) {
      onError(validationError);
      return () => {};
    }
    
    // Keep track of completion status
    let isDone = false;
    let fullResponse = '';
    
    // Connect to WebSocket for chat streaming if not already connected
    const setupWebSocket = () => {
      console.log('Setting up WebSocket for chat streaming');
      
      // Define WebSocket callbacks
      const callbacks = {
        onChatStream: (data: { chunk: string; is_final: boolean }) => {
          // Add chunk to response
          if (data.chunk) {
            fullResponse += data.chunk;
            onMessage(data.chunk);
          }
          
          // Check if final message
          if (data.is_final && !isDone) {
            isDone = true;
            onDone();
          }
        },
        onError: (error: any) => {
          logError('WebSocket error', error);
          if (!isDone) {
            console.log('Falling back to SSE due to WebSocket error');
            setupSSEFallback();
          }
        }
      };
      
      // Connect to the chat WebSocket - ensure both values are strings
      if (request.project_id && request.session_id) {
        websocketService.connectToChat(request.project_id, request.session_id, callbacks);
      } else {
        logError('Cannot connect to chat WebSocket: Missing project_id or session_id');
        onError(new Error('Missing project_id or session_id for WebSocket connection'));
      }
    };
    
    // Function to use SSE as fallback
    const setupSSEFallback = () => {
      console.log('Using SSE fallback for chat streaming');
      
      const baseUrl = apiClient.getBaseUrl();
      const endpointUrl = `${baseUrl}${this.baseUrl}/completion`;
      
      console.log('Setting up SSE connection to:', endpointUrl);
      
      // Make a POST request to start the chat completion
      apiClient.post<string>(`${this.baseUrl}/completion/`, request, {
        responseType: 'text',
        headers: {
          'Accept': 'text/event-stream',
        }
      }).then(response => {
        console.log('Received completion response:', response);
        
        // If we get here, streaming didn't work but we got a full response
        if (typeof response === 'string') {
          onMessage(response);
        } else if (response && typeof response === 'object') {
          // Handle if response is an object
          onMessage(JSON.stringify(response));
        } else {
          // Fallback for any other type
          onMessage(String(response));
        }
        onDone();
      }).catch(error => {
        logError('Failed to send message for streaming', error);
        
        // Fall back to polling if SSE fails
        this.fallbackPolling(request, onMessage, onDone, onError);
      });
    };
    
    // Set up WebSocket for streaming
    setupWebSocket();
    
    // Also make the initial POST request to start the chat completion
    // (the backend will send the responses through both WebSocket and SSE)
    apiClient.post<string>(`${this.baseUrl}/completion/`, request, {
      responseType: 'text',
      headers: {
        'Accept': 'text/event-stream',
      }
    }).catch(error => {
      logError('Failed to send message for streaming', error);
      
      // If WebSocket streaming is also not working, fall back to polling
      if (!isDone) {
        this.fallbackPolling(request, onMessage, onDone, onError);
      }
    });
    
    // Return a function to cancel the request
    return () => {
      console.log('Canceling streaming request');
      isDone = true;
    };
  }
  
  /**
   * Fallback method that polls for updates instead of streaming
   */
  private async fallbackPolling(
    request: ChatCompletionRequest, 
    onMessage: (text: string) => void, 
    onDone: () => void, 
    onError: (error: any) => void
  ): Promise<void> {
    console.log('Using fallback polling method for chat completion');
    
    try {
      // Just make a regular request and return the full result
      const response = await this.sendMessage(request);
      onMessage(response);
      onDone();
    } catch (error) {
      logError('Fallback polling failed', error);
      onError(error);
    }
  }

  /**
   * Send a message and get a completion (non-streaming)
   */
  async sendMessage(request: ChatCompletionRequest): Promise<string> {
    console.log('Sending chat completion request:', request);
    
    // Validate the request before sending
    if (!request.project_id || request.project_id.trim() === '') {
      throw new Error('Project ID is required and cannot be empty');
    }
    
    if (!request.session_id || request.session_id.trim() === '') {
      throw new Error('Session ID is required and cannot be empty');
    }
    
    if (!request.message || request.message.trim() === '') {
      throw new Error('Message is required and cannot be empty');
    }
    
    // Validate ID format (should contain only alphanumeric, hyphens, underscores)
    const idRegex = /^[a-zA-Z0-9_-]+$/;
    if (!idRegex.test(request.project_id)) {
      throw new Error(`Invalid project ID format: "${request.project_id}". Only letters, numbers, hyphens, and underscores are allowed.`);
    }
    
    if (!idRegex.test(request.session_id)) {
      throw new Error(`Invalid session ID format: "${request.session_id}". Only letters, numbers, hyphens, and underscores are allowed.`);
    }
    
    const response = await apiClient.post<{ response: string }>(`${this.baseUrl}/completion`, request);
    return response.response;
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;
